import { defineStore } from "pinia";
import { useAnalyticsStore } from "@/store/analytics.store";
import { Post } from "@/models/post";

export enum PlayerState {
    init,
    playing,
    paused,
    ended,
}

export interface PlaybackDataInterface {
    duration: number;
    currentTime: number;
}

export interface PlayerStoreInterface {
    state: PlayerState;
    queue: Post[];
    player?: HTMLAudioElement;
    playback?: PlaybackDataInterface;
    activePost?: Post;
    currentTrackingPostID?: string;
}

export const usePlayerStore = defineStore({
    id: "player",
    state(): PlayerStoreInterface {
        return {
            state: PlayerState.init,
            queue: [],
            player: undefined,
            playback: undefined,
            activePost: undefined,
            currentTrackingPostID: undefined,
        };
    },
    getters: {
        isPlaying(): boolean {
            return this.state === PlayerState.playing;
        },
        isPaused(): boolean {
            return this.state === PlayerState.paused;
        },
        currentTime(): number {
            if (!this.playback || isNaN(this.playback.duration)) {
                return 0;
            }

            if (this.playback.duration && this.playback.currentTime) {
                return Math.round(this.playback.duration - this.playback.currentTime);
            }

            return Math.round(this.playback.duration);
        },
        originalCurrentTime(): number {
            return this.playback?.currentTime ?? 0;
        },
        duration(): number {
            return this.playback?.duration || 0;
        },
        progress(): number {
            if (this.playback?.duration && this.playback.currentTime) {
                return (this.playback.currentTime / this.playback.duration) * 100;
            }

            return 0;
        },
        getPlayback(): PlaybackDataInterface | undefined {
            return this.playback;
        },
        getActivePost(): Post | undefined {
            return this.activePost;
        },
        getQueue(): Post[] {
            return this.queue;
        },
        volume(): number {
            return this.player?.volume ?? 1;
        },
        loop(): boolean {
            return this.player?.loop ?? false;
        },
        isMuted(): boolean {
            return this.player?.muted ?? false;
        },
        getState(): PlayerState {
            return this.state;
        },
    },
    actions: {
        setQueue(queue: Post[]): void {
            this.queue = queue;
            this.setActivePost(queue[0], false);
        },
        async setActivePost(post: Post, forcePlay: boolean, didEnd?: boolean): Promise<void> {
            // set analytics on post change
            if (this.isPlaying && this.playback && this.activePost && this.activePost.id !== post.id) {
                useAnalyticsStore().trackEvent("audioPlayEnd", this.$analytics.playParams(didEnd ? "completed" : "partial", this.activePost, this.playback));
            }

            this.activePost = post;
            this.preparePlayer(post.audio?.url);
            if (this.isPlaying || forcePlay) {
                await this.play();
            }
        },
        preparePlayer(src?: string): void {
            if (!src) {
                return;
            }

            if (!this.player) {
                this.player = new Audio(src);
                this.player.addEventListener("timeupdate", this.onTimeUpdated);
                this.player.addEventListener("ended", this.onEnded);
                this.player.addEventListener("loadedmetadata", this.onLoadedMetadata);
            } else {
                this.player.src = src;
            }

            this.player.load();
        },
        resetPlayer(): void {
            this.pause();
            this.playback = {
                duration: this.player?.duration ?? 0,
                currentTime: 0,
            };

            if (this.player) {
                this.player.currentTime = 0;
            }
        },
        async play(): Promise<void> {
            if (this.player) {
                await this.player.play();

                // audio play state changed only of changing from pause to play
                if (!this.isPlaying) {
                    useAnalyticsStore().trackEvent("audioPlayStateChanged", { action: "playing" });
                }

                // track begin playback only on new active post
                if (this.activePost && this.playback && this.currentTrackingPostID !== this.activePost.id) {
                    useAnalyticsStore().trackEvent("audioPlayBegin", this.$analytics.playParams("began", this.activePost, this.playback));
                    this.currentTrackingPostID = this.activePost.id;
                }

                this.state = PlayerState.playing;
            }
        },
        setVolume(level: number) {
            if (!this.player) {
                return;
            }
            this.player.volume = level;
        },
        setLoop(loop: boolean) {
            if (!this.player) {
                return;
            }
            this.player.loop = loop;
        },
        setMuted(mute: boolean) {
            if (!this.player) {
                return;
            }
            this.player.muted = mute;
        },
        pause(): void {
            if (this.player) {
                this.player.pause();

                // audio play state changed only of changing from playing to pauses
                if (this.isPlaying) {
                    useAnalyticsStore().trackEvent("audioPlayStateChanged", { action: "paused" });
                }

                this.state = PlayerState.paused;
            }
        },
        async rawPlay(): Promise<void> {
            if (this.player) {
                await this.player.play();
            }
        },
        rawPause(): void {
            if (this.player) {
                this.player.pause();
            }
        },
        setCurrentTime(time: number): void {
            if (this.player) {
                this.player.currentTime = time;
            }
        },
        seek(seekOffset: number): void {
            if (this.player) {
                this.player.currentTime = this.player.duration * seekOffset;
            }
        },
        onTimeUpdated(): void {
            if (this.player) {
                this.playback = {
                    duration: this.player.duration,
                    currentTime: this.player.currentTime,
                };
            }
        },
        onEnded(): void {
            useAnalyticsStore().trackEvent("audioPlayStateChanged", { action: "paused" });
            // track on ended when nothing to play next - special case
            if (this.activePost && this.playback) {
                useAnalyticsStore().trackEvent("audioPlayEnd", this.$analytics.playParams("completed", this.activePost, this.playback));
            }

            this.state = PlayerState.ended;
            this.setActivePost(this.queue[0], false, true);
        },
        onLoadedMetadata(): void {
            if (this.player) {
                this.playback = {
                    duration: this.player.duration,
                    currentTime: 0,
                };
            }
        },
    },
});
